import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ShoppingBag, Search, Menu, X, User, ArrowRight, Truck } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import LuxuryButton from "../ui/LuxuryButton";
import CurvedOfferLoop from "../bits/CurvedOfferLoop";
import { CartService, cartEvents } from "../../api/cart.api";
import { ProductService } from "../../api/product.api";

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [cartItemCount, setCartItemCount] = useState(0);
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  const searchInputRef = useRef(null);
  const searchContainerRef = useRef(null);
  const navigate = useNavigate();

  // Close search when scrolling
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);

      // Close search on scroll
      if (isSearchOpen) {
        setIsSearchOpen(false);
        setSearchQuery("");
        setSearchResults([]);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isSearchOpen]);

  // Debounced search function
  const handleSearch = useCallback(async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const results = await ProductService.searchProducts(query);
      setSearchResults(results || []);
    } catch (error) {
      console.error('Error searching products:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, []);

  // Debounce effect for search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (isSearchOpen && searchQuery.trim()) {
        handleSearch(searchQuery);
      } else {
        setSearchResults([]);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, isSearchOpen, handleSearch]);

  // Focus input when search opens
  useEffect(() => {
    if (isSearchOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isSearchOpen]);

  // Close search when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchContainerRef.current &&
          !searchContainerRef.current.contains(event.target) &&
          isSearchOpen) {
        setIsSearchOpen(false);
        setSearchQuery("");
        setSearchResults([]);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isSearchOpen]);

  // Prevent body scroll when search is open
  useEffect(() => {
    if (isSearchOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isSearchOpen]);

  // Close mobile menu on scroll
  useEffect(() => {
    const handleScrollCloseMenu = () => {
      if (isMenuOpen) {
        setIsMenuOpen(false);
      }
    };

    window.addEventListener("scroll", handleScrollCloseMenu);
    return () => window.removeEventListener("scroll", handleScrollCloseMenu);
  }, [isMenuOpen]);

  // Load cart item count on component mount
  useEffect(() => {
    loadCartItemCount();

    // Subscribe to cart updates
    const unsubscribe = cartEvents.subscribe('cartUpdated', () => {
      loadCartItemCount();
    });

    // Subscribe to specific cart events
    const unsubscribeItemAdded = cartEvents.subscribe('itemAdded', () => {
      loadCartItemCount();
    });

    const unsubscribeItemRemoved = cartEvents.subscribe('itemRemoved', () => {
      loadCartItemCount();
    });

    const unsubscribeCartCleared = cartEvents.subscribe('cartCleared', () => {
      setCartItemCount(0);
    });

    return () => {
      unsubscribe();
      unsubscribeItemAdded();
      unsubscribeItemRemoved();
      unsubscribeCartCleared();
    };
  }, []);

  const loadCartItemCount = async () => {
    try {
      const count = await CartService.getCartItemCount();
      setCartItemCount(count || 0);
    } catch (error) {
      console.error('Error loading cart item count:', error);
      setCartItemCount(0);
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
      setIsSearchOpen(false);
      setSearchQuery("");
      setSearchResults([]);
    }
  };

  const handleSearchResultClick = (product) => {
    navigate(`/product/${product.id}`);
    setIsSearchOpen(false);
    setSearchQuery("");
    setSearchResults([]);
  };

  const handleTrackOrder = () => {
    navigate('/track-order');
  };

  const navItems = [
    { label: "Shop", href: "/shop" },
    { label: "Story", href: "/about" },
    { label: "Blog", href: "/blog" },
    { label: "Journal", href: "/journal" },
    { label: "Stores", href: "/stores" },
  ];

  const popularSearches = ['Vitamin C Serum', 'Moisturizer', 'Cleanser', 'Anti-Aging', 'Skincare Ritual'];

  return (
      <>
        {/* Offer Loop */}
        <CurvedOfferLoop />

        {/* Search Overlay */}
        <AnimatePresence>
          {isSearchOpen && (
              <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="fixed inset-0 bg-white/95 backdrop-blur-xl z-[60] pt-32"
                  onClick={() => {
                    setIsSearchOpen(false);
                    setSearchQuery("");
                    setSearchResults([]);
                  }}
              >
                <div className="container-luxury max-w-4xl mx-auto px-4" ref={searchContainerRef}>
                  <div className="relative" onClick={(e) => e.stopPropagation()}>
                    <form onSubmit={handleSearchSubmit}>
                      <div className="relative">
                        <input
                            ref={searchInputRef}
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Search for products, ingredients, or rituals..."
                            className="w-full text-2xl md:text-4xl font-light bg-transparent border-0 border-b border-nude/50 focus:border-champagne focus:ring-0 py-4 px-2 placeholder:text-charcoal/30 outline-none transition-colors"
                            autoComplete="off"
                            autoCorrect="off"
                            spellCheck="false"
                        />

                        <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center gap-2">
                          {isSearching && (
                              <div className="text-sm text-charcoal/40">
                                Searching...
                              </div>
                          )}

                          <motion.button
                              type="submit"
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.95 }}
                              className="p-3 text-charcoal/60 hover:text-champagne"
                              disabled={!searchQuery.trim()}
                          >
                            <ArrowRight className="w-6 h-6" />
                          </motion.button>
                        </div>
                      </div>
                    </form>

                    {/* Search Results */}
                    {searchQuery.trim() && (
                        <div className="mt-8 max-h-[400px] overflow-y-auto">
                          {searchResults.length > 0 ? (
                              <div>
                                <h4 className="text-sm font-medium text-charcoal/50 mb-4">
                                  Search Results ({searchResults.length})
                                </h4>
                                <div className="space-y-3">
                                  {searchResults.map((product) => (
                                      <motion.button
                                          key={product.id}
                                          type="button"
                                          onClick={() => handleSearchResultClick(product)}
                                          whileHover={{ x: 4 }}
                                          className="w-full text-left p-3 hover:bg-nude/20 rounded-lg transition-colors flex items-center gap-3"
                                      >
                                        {product.image && (
                                            <img
                                                src={product.image}
                                                alt={product.name}
                                                className="w-10 h-10 object-cover rounded-md"
                                            />
                                        )}
                                        <div>
                                          <h5 className="font-medium text-charcoal">
                                            {product.name}
                                          </h5>
                                          <p className="text-sm text-charcoal/60">
                                            {product.category}
                                          </p>
                                        </div>
                                      </motion.button>
                                  ))}
                                </div>
                              </div>
                          ) : !isSearching ? (
                              <div className="text-center py-8">
                                <p className="text-charcoal/60">
                                  No products found for "{searchQuery}"
                                </p>
                              </div>
                          ) : null}
                        </div>
                    )}

                    {/* Popular Searches - Only show when no search query */}
                    {!searchQuery.trim() && (
                        <div className="mt-12">
                          <h4 className="text-sm font-medium text-charcoal/50 mb-4">Popular Searches</h4>
                          <div className="flex flex-wrap gap-3">
                            {popularSearches.map((term, index) => (
                                <motion.button
                                    key={index}
                                    type="button"
                                    onClick={() => {
                                      setSearchQuery(term);
                                      setTimeout(() => {
                                        if (searchInputRef.current) {
                                          searchInputRef.current.focus();
                                        }
                                      }, 100);
                                    }}
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                    className="px-4 py-2 bg-nude/20 text-charcoal/70 rounded-full hover:bg-nude/30 hover:text-charcoal transition-all"
                                >
                                  {term}
                                </motion.button>
                            ))}
                          </div>
                        </div>
                    )}

                    {/* Close Button */}
                    <motion.button
                        onClick={() => {
                          setIsSearchOpen(false);
                          setSearchQuery("");
                          setSearchResults([]);
                        }}
                        className="absolute -top-20 right-4 p-3 text-charcoal/60 hover:text-charcoal"
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                    >
                      <X className="w-6 h-6" />
                    </motion.button>
                  </div>
                </div>
              </motion.div>
          )}
        </AnimatePresence>

        {/* Navbar */}
        <motion.nav
            initial={{ y: -80, opacity: 0 }}
            animate={{
              y: 0,
              opacity: 1,
              paddingTop: isScrolled ? 12 : 24,
              paddingBottom: isScrolled ? 12 : 24,
            }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className={`fixed top-[28px] left-0 right-0 z-50 transition-all duration-700 ${
                isScrolled
                    ? "bg-white/100 backdrop-blur-xl shadow-subtle"
                    : "bg-transparent backdrop-blur-0"
            } ${isSearchOpen ? "hidden" : "block"}`}
        >
          <div className="container-luxury">
            <div className="flex items-center justify-between">
              {/* Logo */}
              <motion.div whileHover={{ scale: 1.05 }}>
                <Link
                    to="/"
                    className="text-2xl font-display font-light tracking-tight text-charcoal"
                >
                  KROMAIC
                </Link>
              </motion.div>

              {/* Desktop Nav */}
              <div className="hidden lg:flex items-center gap-10">
                {navItems.map((item) => (
                    <motion.div key={item.label} whileHover={{ y: -2 }}>
                      <Link
                          to={item.href}
                          className="text-charcoal/70 hover:text-charcoal text-sm font-medium tracking-wide transition-colors"
                      >
                        {item.label}
                      </Link>
                    </motion.div>
                ))}
              </div>

              {/* Actions */}
              <div className="flex items-center gap-4">
                {/* Track Order Button - Now redirects to tracking page */}
                <motion.button
                    onClick={handleTrackOrder}
                    className="p-2 text-charcoal/70 hover:text-charcoal relative group"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    aria-label="Track Order"
                >
                  <Truck className="w-5 h-5 transition-transform group-hover:-translate-y-0.5" />
                  <span className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 whitespace-nowrap px-2 py-1 bg-charcoal text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                  Track Order
                </span>
                </motion.button>

                {/* Search Button */}
                <motion.button
                    onClick={() => setIsSearchOpen(true)}
                    className="p-2 text-charcoal/70 hover:text-charcoal relative group"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    aria-label="Search"
                >
                  <Search className="w-5 h-5 transition-transform group-hover:-translate-y-0.5" />
                  {searchQuery && !isSearchOpen && (
                      <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          className="absolute -top-1 -right-1 w-2 h-2 bg-champagne rounded-full"
                      />
                  )}
                  <span className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 whitespace-nowrap px-2 py-1 bg-charcoal text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                  Search
                </span>
                </motion.button>

                {/* User Account */}
                <Link to="/login" className="group relative">
                  <motion.div
                      className="p-2 text-charcoal/70 hover:text-charcoal"
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      aria-label="Account"
                  >
                    <User className="w-5 h-5 transition-transform group-hover:-translate-y-0.5" />
                  </motion.div>
                  <span className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 whitespace-nowrap px-2 py-1 bg-charcoal text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                  Account
                </span>
                </Link>

                {/* Cart Button with Dynamic Count */}
                <Link to="/cart" className="group relative">
                  <motion.div
                      className="relative p-2 text-charcoal/70 hover:text-charcoal"
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      aria-label="Cart"
                  >
                    <ShoppingBag className="w-5 h-5 transition-transform group-hover:-translate-y-0.5" />

                    {/* Cart Badge - Only show if count > 0 */}
                    {cartItemCount > 0 && (
                        <motion.span
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            exit={{ scale: 0 }}
                            className="absolute -top-1 -right-1 min-w-[20px] h-5 bg-champagne text-white text-xs rounded-full flex items-center justify-center px-1"
                        >
                          {cartItemCount > 99 ? '99+' : cartItemCount}
                        </motion.span>
                    )}

                    {/* Optional: Show subtle indicator even when count is 0 */}
                    {cartItemCount === 0 && (
                        <div className="absolute -top-1 -right-1 w-1.5 h-1.5 bg-champagne/30 rounded-full" />
                    )}
                  </motion.div>
                  <span className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 whitespace-nowrap px-2 py-1 bg-charcoal text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                  Cart{cartItemCount > 0 ? ` (${cartItemCount})` : ''}
                </span>
                </Link>

                {/* Mobile Toggle */}
                <motion.button
                    className="lg:hidden p-2 text-charcoal group relative"
                    onClick={() => setIsMenuOpen(!isMenuOpen)}
                    whileTap={{ scale: 0.9 }}
                    aria-label="Menu"
                >
                  {isMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                  <span className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 whitespace-nowrap px-2 py-1 bg-charcoal text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                  {isMenuOpen ? 'Close' : 'Menu'}
                </span>
                </motion.button>
              </div>
            </div>
          </div>
        </motion.nav>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMenuOpen && !isSearchOpen && (
              <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="fixed top-[96px] left-0 right-0 z-40 bg-white/95 backdrop-blur-xl lg:hidden overflow-hidden"
              >
                <div className="container-luxury py-8">
                  <div className="space-y-6">
                    {/* Track Order in Mobile Menu */}
                    <div className="pb-4 border-b border-nude/50">
                      <button
                          onClick={() => {
                            setIsMenuOpen(false);
                            handleTrackOrder();
                          }}
                          className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-nude/10 transition-colors text-left"
                      >
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-champagne/20 to-sage/20 flex items-center justify-center">
                          <Truck className="w-5 h-5 text-champagne" />
                        </div>
                        <div>
                          <p className="font-medium text-charcoal">Track Order</p>
                          <p className="text-sm text-charcoal/50">Check order status</p>
                        </div>
                      </button>
                    </div>

                    {/* Search in Mobile Menu */}
                    <div className="pb-4 border-b border-nude/50">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-charcoal/40" />
                        <input
                            type="text"
                            placeholder="Search..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            onFocus={() => {
                              setIsMenuOpen(false);
                              setIsSearchOpen(true);
                            }}
                            className="w-full pl-10 pr-4 py-3 bg-nude/20 rounded-xl border border-nude/30 focus:outline-none focus:border-champagne text-sm"
                        />
                      </div>
                    </div>

                    {/* Navigation Links */}
                    {navItems.map((item, index) => (
                        <motion.div
                            key={item.label}
                            initial={{ x: -20, opacity: 0 }}
                            animate={{ x: 0, opacity: 1 }}
                            transition={{ duration: 0.4, delay: index * 0.05 }}
                        >
                          <Link
                              to={item.href}
                              onClick={() => setIsMenuOpen(false)}
                              className="block text-lg font-medium text-charcoal hover:text-sage transition-colors py-2"
                          >
                            {item.label}
                          </Link>
                        </motion.div>
                    ))}

                    {/* Cart Count in Mobile Menu */}
                    <div className="py-4 border-t border-nude/50">
                      <Link
                          to="/cart"
                          onClick={() => setIsMenuOpen(false)}
                          className="flex items-center justify-between hover:bg-nude/10 p-3 rounded-xl transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <ShoppingBag className="w-5 h-5 text-charcoal/70" />
                          <div>
                            <p className="font-medium text-charcoal">Cart</p>
                            <p className="text-sm text-charcoal/50">View your items</p>
                          </div>
                        </div>
                        {cartItemCount > 0 ? (
                            <span className="px-2 py-1 bg-champagne text-white text-xs rounded-full">
                        {cartItemCount} {cartItemCount === 1 ? 'item' : 'items'}
                      </span>
                        ) : (
                            <span className="text-sm text-charcoal/40">Empty</span>
                        )}
                      </Link>
                    </div>

                    {/* Account Links */}
                    <div className="pt-4 border-t border-nude/50 space-y-3">
                      <Link
                          to="/login"
                          onClick={() => setIsMenuOpen(false)}
                          className="flex items-center gap-3 p-3 rounded-xl hover:bg-nude/10 transition-colors"
                      >
                        <User className="w-5 h-5 text-charcoal/70" />
                        <div>
                          <p className="font-medium text-charcoal">Account</p>
                          <p className="text-sm text-charcoal/50">Sign in or register</p>
                        </div>
                      </Link>
                      <Link
                          to="/orders"
                          onClick={() => setIsMenuOpen(false)}
                          className="flex items-center gap-3 p-3 rounded-xl hover:bg-nude/10 transition-colors"
                      >
                        <ShoppingBag className="w-5 h-5 text-charcoal/70" />
                        <div>
                          <p className="font-medium text-charcoal">Orders</p>
                          <p className="text-sm text-charcoal/50">View order history</p>
                        </div>
                      </Link>
                    </div>

                    {/* CTA Button */}
                    <div className="pt-6 border-t border-nude">
                      <LuxuryButton
                          fullWidth
                          onClick={() => {
                            setIsMenuOpen(false);
                            navigate('/shop');
                          }}
                      >
                        Start Your Ritual
                      </LuxuryButton>
                    </div>
                  </div>
                </div>
              </motion.div>
          )}
        </AnimatePresence>
      </>
  );
};

export default Navbar;